import React from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Drawer, FormInstance } from "antd";
import EventsHistory from "components/eventsHistory/EventsHistory";
import DrawerForm from "components/form";
import FormCheckin from "components/form/checkin";
import {
  IDocumentsData,
  IDocumentType,
  IEntryPermissionActive,
  IProcessesData,
  IRegisterActiveItem,
  ISaleChannelData,
  IStepEntryPermissionStatus,
  IVehicleProfileByPlate,
  PlateFormatType,
} from "store/lobby/lobby-model";
import { IUserData } from "store/user-session/user-session-model";
import { IEventsVehicleHistory } from "typings/global";
import { DrawerStatusEnum } from "typings/global.types";
import { isRoleAuthorizedToFinishVehicle, isRoleAuthorizedToEditEntry } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import YmsDrawer from "./../../../components/drawer";
import CheckoutForm from "./components/checkoutForm";
import { ConfirmInbound, ConfirmViewEdit } from "./components/confirm";
import ViewRegister from "./components/viewRegister";
import { OnConfirmCancelEdit } from "./lobby.service";
import * as S from "./styled/index.styled";

interface IPropsScreenDrawers {
  visibleDrawerVehicleEntryAuthorization: boolean;
  vehicleEntryAuthorizationRegisterActive: Array<IEventsVehicleHistory>;
  checkinRegisterExisting: IRegisterActiveItem;
  vehicleProfileByPlate?: IVehicleProfileByPlate | null;
  visibleDrawerCheckinRegister?: boolean;
  hasSaleChannel: boolean;
  disabledButton: boolean;
  form: FormInstance;
  isConcierge: boolean;
  salesChannelList: Array<ISaleChannelData>;
  documentsList: IDocumentsData[];
  inboundProcessesList: Array<IProcessesData>;
  outboundProcessesList: Array<IProcessesData>;
  visibleDrawerInbound?: boolean;
  visibleDrawerOutbound?: boolean;
  visibleDrawerViewRegister?: boolean;
  visibleDrawerViewMode?: DrawerStatusEnum;
  formViewOrEditModified?: boolean;
  drawerIsLoading: boolean;
  carrierRef: any;
  YES_MESSAGE: string;
  NO_MESSAGE: string;
  formOutbound: FormInstance;
  formView: FormInstance;
  formCheckin: FormInstance;
  userData: IUserData;
  hasEntryPermission: boolean;
  isLoadingEntryPermission: boolean;
  documentType?: IDocumentType;
  stepEntryPermissionStatus?: IStepEntryPermissionStatus;
  entryPermissionActive: IEntryPermissionActive;
  saleChannelActive: number;
  processActive: number;
  plateFormatType: PlateFormatType;
}

interface IDispatchProps {
  handleOnConfirmDrawerInbound: (...args) => void;
  lobbyHideDrawerInbound: (...args) => void;
  lobbyHideDrawerOutbound: () => void;
  lobbyHideDrawerViewRegister: () => void;
  lobbyDocumentsFetchAsync: () => void;
  visibleFocus: (...args) => void;
  handleOnConfirmDrawerOutbound: (...args) => void;
  handleOnConfirmDrawerViewRegister: (...args) => void;
  handleOnConfirmDrawerInboundFromCheckin: (...args) => void;
  handleOnConfirmFinishVehicle: (...args) => void;
  lobbyHideDrawerCheckinRegister: () => void;
  lobbyProcessActive: (...args) => void;
  lobbyOutboundProcessActive: (...args) => void;
  lobbySaleChannelActive: (...args) => void;
  lobbyFetchItemCheckinByVehiclePlateAsync: (...args) => void;
  lobbyFetchVehicleProfileByPlateAsync?: (...args) => void;
  lobbyUpdateVehicleProfileByPlate?: (...args) => void;
  lobbyHideDrawerVehicleEntryAuthorization: (...args) => void;
  lobbyUpdateFormModified: (...args) => void;
  lobbyFetchEntryPermissionAsync: (...args) => void;
  lobbyUpdateStepEntryPermissionStatus: (...args) => void;
  lobbyUpdateEntryPermissionActive: (...args) => void;
}

type IDrawers = IDispatchProps & IPropsScreenDrawers;

const Drawers = ({
  isConcierge,
  hasSaleChannel,
  processActive,
  userData,
  visibleDrawerInbound,
  visibleDrawerOutbound,
  visibleDrawerViewMode,
  visibleDrawerViewRegister,
  visibleDrawerVehicleEntryAuthorization,
  formViewOrEditModified,
  disabledButton,
  documentsList,
  salesChannelList,
  inboundProcessesList,
  outboundProcessesList,
  drawerIsLoading,
  form,
  formView,
  formCheckin,
  carrierRef,
  YES_MESSAGE,
  NO_MESSAGE,
  formOutbound,
  visibleDrawerCheckinRegister,
  checkinRegisterExisting,
  vehicleProfileByPlate,
  vehicleEntryAuthorizationRegisterActive,
  documentType,
  hasEntryPermission,
  stepEntryPermissionStatus,
  isLoadingEntryPermission,
  entryPermissionActive,
  saleChannelActive,
  plateFormatType,
  visibleFocus,
  lobbySaleChannelActive,
  lobbyHideDrawerOutbound,
  lobbyHideDrawerInbound,
  lobbyHideDrawerViewRegister,
  lobbyDocumentsFetchAsync,
  handleOnConfirmDrawerInbound,
  handleOnConfirmDrawerOutbound,
  handleOnConfirmDrawerViewRegister,
  handleOnConfirmDrawerInboundFromCheckin,
  handleOnConfirmFinishVehicle,
  lobbyHideDrawerCheckinRegister,
  lobbyHideDrawerVehicleEntryAuthorization,
  lobbyProcessActive,
  lobbyOutboundProcessActive,
  lobbyUpdateFormModified,
  lobbyFetchItemCheckinByVehiclePlateAsync,
  lobbyFetchVehicleProfileByPlateAsync,
  lobbyUpdateVehicleProfileByPlate,
  lobbyFetchEntryPermissionAsync,
  lobbyUpdateStepEntryPermissionStatus,
  lobbyUpdateEntryPermissionActive,
}: IDrawers): JSX.Element => {
  const FINISH_VEHICLE_TITLE = useTranslation({ id: "finish_vehicle" });
  const optionsViewOrEditDrawer = {
    VIEW: { title: useTranslation({ id: "view_entry" }), confirmbuttontxt: useTranslation({ id: "edit" }), customrenderconfirmbutton: null },
    EDIT: {
      title: useTranslation({ id: "edit_entry" }),
      confirmbuttontxt: useTranslation({ id: "save" }),
      customrenderconfirmbutton: <ConfirmViewEdit handleOnConfirm={() => handleOnConfirmDrawerViewRegister()} okText={YES_MESSAGE} cancelText={NO_MESSAGE} />,
    },
  };
  return (
    <>
      <YmsDrawer
        width={hasEntryPermission ? "1150" : "900"}
        confirmbuttontxt={useTranslation({ id: "register" })}
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        maskClosable={true}
        onConfirm={handleOnConfirmDrawerInbound}
        disabled={disabledButton}
        afterOpenChange={visibleFocus}
        title={useTranslation({ id: "check_in" })}
        placement="right"
        closable={false}
        onClose={lobbyHideDrawerInbound}
        open={visibleDrawerInbound}
      >
        <DrawerForm
          form={form}
          inboundProcessesList={inboundProcessesList}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          documentsList={documentsList}
          visibleDrawerView={visibleDrawerViewRegister}
          carrierRef={carrierRef}
          lobbyProcessActive={lobbyProcessActive}
          hasSaleChannel={hasSaleChannel}
          lobbySaleChannelActive={lobbySaleChannelActive}
          salesChannelList={salesChannelList}
          checkinRegisterExisting={checkinRegisterExisting}
          vehicleProfileByPlate={vehicleProfileByPlate}
          lobbyFetchItemCheckinByVehiclePlateAsync={lobbyFetchItemCheckinByVehiclePlateAsync}
          lobbyFetchVehicleProfileByPlateAsync={lobbyFetchVehicleProfileByPlateAsync}
          lobbyUpdateVehicleProfileByPlate={lobbyUpdateVehicleProfileByPlate}
          visibleDrawerAddNewRegister={visibleDrawerInbound}
          visibleDrawerViewMode={visibleDrawerViewMode}
          drawerIsLoading={drawerIsLoading}
          documentType={documentType}
          hasEntryPermission={hasEntryPermission}
          stepEntryPermissionStatus={stepEntryPermissionStatus}
          lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
          isLoadingEntryPermission={isLoadingEntryPermission}
          entryPermissionActive={entryPermissionActive}
          lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
          lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
          saleChannelActive={saleChannelActive}
          plateFormatType={plateFormatType}
          processActive={processActive}
        />
      </YmsDrawer>
      <YmsDrawer
        onClose={lobbyHideDrawerOutbound}
        open={visibleDrawerOutbound}
        width="900"
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        customrenderconfirmbutton={
          <ConfirmInbound handleOnConfirm={() => handleOnConfirmDrawerOutbound()} okText={YES_MESSAGE} cancelText={NO_MESSAGE} disabled={drawerIsLoading} />
        }
        maskClosable={true}
        title={useTranslation({ id: "check_out" })}
        placement="right"
        closable={false}
        disabled={drawerIsLoading}
      >
        <CheckoutForm
          formCheckout={formOutbound}
          outboundProcessesList={outboundProcessesList}
          documentsList={documentsList}
          lobbyOutboundProcessActive={lobbyOutboundProcessActive}
          visibleDrawerOutbound={visibleDrawerOutbound}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          drawerIsLoading={drawerIsLoading}
          plateFormatType={plateFormatType}
        />
      </YmsDrawer>
      <YmsDrawer
        title={optionsViewOrEditDrawer[visibleDrawerViewMode].title}
        placement="right"
        closable={false}
        open={visibleDrawerViewRegister}
        width="900"
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        confirmbuttontxt={optionsViewOrEditDrawer[visibleDrawerViewMode].confirmbuttontxt}
        customrenderconfirmbutton={optionsViewOrEditDrawer[visibleDrawerViewMode].customrenderconfirmbutton}
        maskClosable={true}
        disabled={drawerIsLoading || !isRoleAuthorizedToFinishVehicle(userData.role)}
        destroyOnClose={true}
        onConfirm={handleOnConfirmDrawerViewRegister}
        onClose={lobbyHideDrawerViewRegister}
        showConfirmButton={isRoleAuthorizedToEditEntry(userData.role)}
        showFinishVehicleButton={isRoleAuthorizedToFinishVehicle(userData.role)}
        finishVehicleButtontxt={FINISH_VEHICLE_TITLE}
        onFinishVehicle={handleOnConfirmFinishVehicle}
        customrendercancelbutton={
          <OnConfirmCancelEdit
            handleOnConfirm={() => lobbyHideDrawerViewRegister()}
            okText={YES_MESSAGE}
            cancelText={NO_MESSAGE}
            visibleDrawerViewMode={visibleDrawerViewMode}
            formViewOrEditModified={formViewOrEditModified}
            lobbyHideDrawerViewRegister={lobbyHideDrawerViewRegister}
            disabled={drawerIsLoading}
          />
        }
      >
        <ViewRegister
          form={formView}
          documentsList={documentsList}
          inboundProcessesList={inboundProcessesList}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          hasSaleChannel={hasSaleChannel}
          lobbySaleChannelActive={lobbySaleChannelActive}
          salesChannelList={salesChannelList}
          lobbyUpdateFormModified={lobbyUpdateFormModified}
          lobbyProcessActive={lobbyProcessActive}
          visibleDrawerViewMode={visibleDrawerViewMode}
          visibleDrawerViewRegister={visibleDrawerViewRegister}
          documentType={documentType}
          saleChannelActive={saleChannelActive}
          plateFormatType={plateFormatType}
        />
      </YmsDrawer>
      <YmsDrawer
        width={hasEntryPermission ? "1150" : "900"}
        title={useTranslation({ id: "check_in" })}
        placement="right"
        closable={false}
        open={visibleDrawerCheckinRegister}
        cancelbuttontxt={useTranslation({ id: "cancel" })}
        confirmbuttontxt={useTranslation({ id: "register" })}
        maskClosable={true}
        disabled={!isConcierge || drawerIsLoading || disabledButton}
        onConfirm={handleOnConfirmDrawerInboundFromCheckin}
        onClose={lobbyHideDrawerCheckinRegister}
        destroyOnClose={true}
      >
        <FormCheckin
          form={formCheckin}
          documentsList={documentsList}
          lobbyDocumentsFetchAsync={lobbyDocumentsFetchAsync}
          visibleDrawerCheckinRegister={visibleDrawerCheckinRegister}
          lobbyProcessActive={lobbyProcessActive}
          inboundProcessesList={inboundProcessesList}
          hasSaleChannel={hasSaleChannel}
          lobbySaleChannelActive={lobbySaleChannelActive}
          salesChannelList={salesChannelList}
          hasItems={false}
          drawerIsLoading={drawerIsLoading}
          documentType={documentType}
          hasEntryPermission={hasEntryPermission}
          lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
          lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
          lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
          saleChannelActive={saleChannelActive}
          plateFormatType={plateFormatType}
          processActive={processActive}
        />
      </YmsDrawer>
      <Drawer
        title={useTranslation({ id: "events_history" })}
        placement="right"
        open={visibleDrawerVehicleEntryAuthorization}
        width="400"
        maskClosable={true}
        closable={false}
        onClose={lobbyHideDrawerVehicleEntryAuthorization}
        destroyOnClose={true}
        extra={
          <S.CloseDrawer onClick={lobbyHideDrawerVehicleEntryAuthorization}>
            <CloseOutlined className="closeIcon" />
          </S.CloseDrawer>
        }
      >
        <EventsHistory data={vehicleEntryAuthorizationRegisterActive} drawerIsLoading={drawerIsLoading} />
      </Drawer>
    </>
  );
};

export default Drawers;
